import { axiosInstance } from "../../../../services/v2/axiosInstance";
import CoursesService from "../../../../services/campus/courses.service";
import PeriodsService from "../../../../services/campus/periods.service";
import BuildingsService from "../../../../services/campus/buildings.service";
import SchedulesService from "../../../../services/campus/schedules.service";
import coursePlanService from "../../../../services/v2/coursePlan.service";
import { CoursePlanDTO } from "../../../../models/CoursePlanDTO";
import store from "../../../../globals";

/*
	Horarios Claros: llamadas y reglas de horas del formulario «Nuevo / Editar Horario» (contrato en docs/HORARIOS_CLAROS_BRIEF.md).
	Guardar sigue siendo el v1 de siempre con el mismo payload que schedulePrompt.tsx; lo nuevo es `seccion` y `forzarChoque`.
*/

/** Mientras se termina de ordenar el plantel, solo Super Admin puede guardar un choque de aula, con motivo. Apagar después. */
export const PERMITIR_CHOQUE_SUPERADMIN = true;

export interface Sesion { dia: number; inicio: string; fin: string }   // dia 0 = Lunes; "11:00"
export interface Conflicto {
	tipo: "aula" | "maestra"; scheduleId: number; curso: string; modulo: string; hora: string; aula: string;
	maestra: string; alumnas: number; fin: string;
}
export interface Validacion { hora: string; fin: string | null; conflictos: Conflicto[]; errores: string[] }
export interface Bloque { scheduleId: number; curso: string; modulo: string; inicio: string; fin: string; alumnas: number; maestra: string; estado: string; choque: boolean }
export interface AulaOcupada { id: number; nombre: string; edificio: string; bloques: Bloque[] }
export interface AulaOpcion { id: number; name: string; edificio: string; edificioId: number }
export interface Periodo { id: number; name: string; start: string; end: string }
export interface Mentor { id: number; name: string; activo: boolean }
export interface NivelCat {
	id: number; name: string; code: string; mentores: Mentor[];
	base: { inscripcion: number; mensualidad: number; recargoInscripcion: number; recargoMensualidad: number };
}
export interface CursoCat { id: number; name: string; code: string; niveles: NivelCat[] }

/** Horario ya guardado, normalizado para el formulario. */
export interface HorarioCargado {
	id: number; cursoId: number; nivelId: number; periodoId: number | null; aulaId: number | null; edificioId: number | null; maestraId: number | null;
	seccion: string; sesiones: Sesion[]; inicio: string; fin: string | null; meses: number | null; statusId: number;
	min: number; max: number; notas: string; tieneListas: boolean; tieneMatriculas: boolean; nombre: string;
	inscripcion: number; mensualidad: number; recargoInscripcion: number; recargoMensualidad: number;
	ajuste: number | null; reposicion: number | null; inscripcionPropia: number | null; venceDia: number; tarifaMaestra: number;
}

const campusUrl = (campusId: number) => `/admin/campus/${campusId}/schedules`;

// -------------------------------------------------------------------------------------------------- horas
export const DIAS_CORTOS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];
export const DIAS_LARGOS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
export const DIAS_PLURAL = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábados", "Domingos"];
export const minutos = (hhmm: string): number => { const [h, m] = hhmm.split(":"); return (+h) * 60 + (+m || 0); };
export const aHHMM = (min: number): string => String(Math.floor(min / 60)).padStart(2, "0") + ":" + String(min % 60).padStart(2, "0");
/** «15:00» → «3:00 pm». */
export const hora12 = (hhmm: string): string => {
	const min = minutos(hhmm);
	const h = Math.floor(min / 60), m = min % 60;
	return ((h % 12) || 12) + ":" + String(m).padStart(2, "0") + (h < 12 ? " am" : " pm");
};
/** De 7:00 am a 10:00 pm, cada 30 minutos. */
export const TIEMPOS: string[] = Array.from({ length: 31 }, (_, i) => aHHMM(420 + i * 30));

/** Texto de respaldo para el nombre (el servidor manda el oficial en `hora`): «Sábados 11:00 am - 3:00 pm». */
export const horaTexto = (sesiones: Sesion[]): string => {
	const grupos: { clave: string; dias: number[]; inicio: string; fin: string }[] = [];
	[...sesiones].sort((a, b) => a.dia - b.dia).forEach((s) => {
		const clave = s.inicio + "-" + s.fin;
		const g = grupos.find((x) => x.clave === clave);
		if (g) g.dias.push(s.dia); else grupos.push({ clave, dias: [s.dia], inicio: s.inicio, fin: s.fin });
	});
	return grupos.map((g) => {
		const nombres = g.dias.map((d) => DIAS_PLURAL[d]);
		const dias = nombres.length > 1 ? nombres.slice(0, -1).join(", ") + " y " + nombres[nombres.length - 1] : nombres[0];
		return dias + " " + hora12(g.inicio) + " - " + hora12(g.fin);
	}).join(", ");
};

/** Las llaves de media hora que espera el v1: día*1000 + hora*10 (+5 si es y media), una por cada media hora de la sesión. */
export const horasV1 = (sesiones: Sesion[]): string => {
	const lista: number[] = [];
	sesiones.forEach((s) => {
		for (let t = minutos(s.inicio); t < minutos(s.fin); t += 30) {
			lista.push(s.dia * 1000 + Math.floor(t / 60) * 10 + (t % 60 === 30 ? 5 : 0));
		}
	});
	return lista.sort((a, b) => a - b).map((n) => String(n).padStart(4, "0")).join(",");
};

/** «Lu 09:00-12:00, Ma 09:00-12:00» (como lo manda _schedules) → sesiones. */
export const sesionesDeTexto = (texto: string): Sesion[] => {
	const salida: Sesion[] = [];
	String(texto || "").split(",").forEach((trozo) => {
		const m = trozo.trim().match(/^(\S+)\s+(\d{1,2}:\d{2})-(\d{1,2}:\d{2})$/);
		if (!m) return;
		const dia = DIAS_CORTOS.findIndex((d) => d.normalize("NFD").replace(/[̀-ͯ]/g, "") === m[1].normalize("NFD").replace(/[̀-ͯ]/g, ""));
		if (dia >= 0) salida.push({ dia, inicio: aHHMM(minutos(m[2])), fin: aHHMM(minutos(m[3])) });
	});
	return salida;
};

/** «2027-03-13» o «13/03/2027» → «13/03/2027». */
export const fechaCorta = (f: string | null | undefined): string => {
	if (!f) return "";
	const s = String(f).slice(0, 10);
	if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s.slice(8, 10) + "/" + s.slice(5, 7) + "/" + s.slice(0, 4);
	return s;
};
/** «13/03/2027» o «2027-03-13» → «2027-03-13». */
export const fechaISO = (f: string | null | undefined): string => {
	const s = String(f || "").slice(0, 10);
	const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
	return m ? m[3] + "-" + m[2] + "-" + m[1] : s;
};

// -------------------------------------------------------------------------------------------------- lecturas
let cacheCatalogo: { campusId: number; hasta: number; datos: Promise<CursoCat[]> } | null = null;

/** Cursos activos con sus módulos, las maestras de cada módulo y los precios base (el mismo pedido que hace Horarios hoy). */
export const cargarCatalogo = (campusId: number, sufijoMoneda: string): Promise<CursoCat[]> => {
	if (cacheCatalogo && cacheCatalogo.campusId === campusId && cacheCatalogo.hasta > Date.now()) return cacheCatalogo.datos;
	const num = (v: any) => (v == null || v === "" ? 0 : +v);
	const datos = CoursesService.searchCourses({ id: campusId }, { statusId: 1 }, 1).then((r: any) =>
		(r.data || []).map((c: any): CursoCat => ({
			id: c.id, name: c.name, code: String(c.code || ""),
			niveles: (c.levels || []).map((l: any): NivelCat => ({
				id: l.id, name: l.name, code: String(l.code || ""),
				mentores: (l.mentors || []).map((m: any): Mentor => ({ id: m.id, name: ((m.first_name || "") + " " + (m.last_name || "")).trim(), activo: (+m.bool_enabled) > 0 })),
				base: {
					inscripcion: num(l["subscription_base_price" + sufijoMoneda]), mensualidad: num(l["monthly_billing_base_price" + sufijoMoneda]),
					recargoInscripcion: num(l["subscription_surcharge_base_cost" + sufijoMoneda]), recargoMensualidad: num(l["monthly_billing_surcharge_base_cost" + sufijoMoneda])
				}
			}))
		}))
	);
	datos.catch(() => { cacheCatalogo = null; });
	cacheCatalogo = { campusId, hasta: Date.now() + 120000, datos };
	return datos;
};

export const cargarPeriodos = (campusId: number): Promise<Periodo[]> =>
	PeriodsService.searchPeriods(campusId).then((r: any) => (Array.isArray(r.data) ? r.data : (r.data?.data || [])).map((p: any): Periodo => ({
		id: p.id, name: p.name, start: String(p.start || "").slice(0, 10), end: String(p.end || "").slice(0, 10)
	})));

/** Aulas del plantel con su edificio: _opciones del contrato; si aún no responde, los edificios de siempre. */
export const cargarAulas = async (campusId: number): Promise<AulaOpcion[]> => {
	try {
		const r = await axiosInstance.get(`${campusUrl(campusId)}/_opciones`);
		if (Array.isArray(r.data?.aulas)) return r.data.aulas.map((a: any): AulaOpcion => ({ id: a.id, name: a.name, edificio: a.edificio || "", edificioId: a.edificioId }));
	} catch (e) { /* cae al respaldo */ }
	const r: any = await BuildingsService.searchBuildings(campusId, null, "classrooms");
	const salida: AulaOpcion[] = [];
	(r.data || []).forEach((b: any) => (b.classrooms || []).forEach((a: any) => salida.push({ id: a.id, name: a.name, edificio: b.name, edificioId: b.id })));
	return salida;
};

export const cargarPlan = (campusId: number, cursoId: number): Promise<CoursePlanDTO | null> =>
	coursePlanService.getPlan(campusId, cursoId).then((r) => r.data || null).catch(() => null);

export const validar = async (campusId: number, p: { id?: number | null; aulaId?: number | null; maestraId?: number | null; inicio?: string; sesiones: Sesion[]; nivelId?: number | null; meses?: number | string | null }): Promise<Validacion> => {
	const params: any = { sessions: JSON.stringify(p.sesiones) };
	if (p.id) params.id = p.id;
	if (p.aulaId) params.classroomId = p.aulaId;
	params.mentorId = p.maestraId ? p.maestraId : ""; // vacío = sin maestra (no la que tenía guardada)
	if (Number(p.meses) > 0) params.months = Number(p.meses);
	if (p.inicio) params.start = p.inicio;
	if (p.nivelId) params.levelId = p.nivelId;
	const r = await axiosInstance.get(`${campusUrl(campusId)}/_validar`, { params });
	const d = r.data || {};
	return { hora: d.hora || "", fin: d.fin || null, conflictos: Array.isArray(d.conflictos) ? d.conflictos : [], errores: Array.isArray(d.errores) ? d.errores : [] };
};

export const ocupacion = async (campusId: number, weekDay: number): Promise<AulaOcupada[]> => {
	const r = await axiosInstance.get(`${campusUrl(campusId)}/_ocupacion`, { params: { weekDay } });
	return Array.isArray(r.data?.aulas) ? r.data.aulas : [];
};

const dinero = (m: any): number => (m && typeof m === "object" ? +m.value || 0 : +m || 0);

/** Horario guardado: _schedules (campos crudos) + _ficha (sesiones, sección) cuando el servidor ya la tiene. */
export const cargarHorario = async (campusId: number, id: number): Promise<HorarioCargado> => {
	const dto = (await axiosInstance.get(`/admin/campus/${campusId}/_schedules`, { params: { perPage: 1, criteria: JSON.stringify({ id }) } })).data?.data?.[0];
	if (!dto) throw new Error("No se encontró el horario.");
	let ficha: any = null;
	try { ficha = (await axiosInstance.get(`${campusUrl(campusId)}/${id}/_ficha`)).data; } catch (e) { ficha = null; }
	const sesiones: Sesion[] = Array.isArray(ficha?.sesiones) && ficha.sesiones.length
		? ficha.sesiones.map((s: any): Sesion => ({ dia: +s.dia, inicio: aHHMM(minutos(s.inicio)), fin: aHHMM(minutos(s.fin)) }))
		: sesionesDeTexto(dto.sessions);
	const nombre: string = String(ficha?.nombre || dto.name || "");
	const seccion = String(ficha?.seccion || (nombre.match(/secci[oó]n\s+([\p{L}\p{N}]+)/iu) || [])[1] || "").toUpperCase();
	return {
		id, cursoId: dto.courseId, nivelId: dto.levelId, periodoId: dto.periodId ?? null, aulaId: dto.classroomId ?? null, edificioId: dto.buildingId ?? null,
		maestraId: dto.mentorId ? +dto.mentorId : null, seccion, sesiones, inicio: fechaISO(dto.startDate), fin: fechaISO(dto.endDate) || null,
		meses: dto.billableMonths ?? null, statusId: dto.statusId, min: +dto.studentsMinimum || 1, max: +dto.studentsLimit || 1, notas: dto.notes || "",
		tieneListas: !!dto.hasAttendances || (ficha?.asistencia != null && ficha.asistencia !== false && !!(ficha.ultimaLista)), tieneMatriculas: !!dto.hasEnrollments, nombre,
		inscripcion: dinero(dto.subscriptionPrice), mensualidad: dinero(dto.monthlyBillingPrice),
		recargoInscripcion: dinero(dto.subscriptionSurchargeCost), recargoMensualidad: dinero(dto.monthlyBillingSurchargeCost),
		ajuste: dto.monthlyPriceAdjustment == null ? null : +dto.monthlyPriceAdjustment, reposicion: dto.recoverySessionPrice == null ? null : +dto.recoverySessionPrice,
		inscripcionPropia: dto.subscriptionPriceOverride == null ? null : +dto.subscriptionPriceOverride,
		venceDia: +dto.firstSurchargeMonthDay || 20, tarifaMaestra: dinero(dto.mentorFee)
	};
};

// -------------------------------------------------------------------------------------------------- guardar
export interface DatosGuardar {
	id: number | null; cursoOriginal: number; nivelOriginal: number; nivelId: number; nombre: string; seccion: string; notas: string;
	inscripcion: number; mensualidad: number; recargoInscripcion: number; recargoMensualidad: number;
	reposicion: string; ajuste: string; tarifaMaestra: string; venceDia: string; meses: string; max: string; min: string;
	periodoId: number; inicio: string; statusId: number; aulaId: number; maestraId: number | null; sesiones: Sesion[];
	inscripcionPropia: string | null; forzarMotivo: string | null;
}

/** Mismo payload y mismo endpoint v1 que schedulePrompt.tsx (SchedulesService.saveSchedule), más `seccion` y `forzarChoque`. */
export const guardar = (campusId: number, d: DatosGuardar) => {
	const record: any = {
		name: d.nombre,
		notes: d.notas,
		subscriptionPrice: d.inscripcion,
		monthlyBillingPrice: d.mensualidad,
		subscriptionSurchargeCost: d.recargoInscripcion,
		monthlyBillingSurchargeCost: d.recargoMensualidad,
		recoverySessionPrice: d.reposicion,
		monthlyPriceAdjustment: d.ajuste,
		mentorFee: d.tarifaMaestra,
		firstSurchargeMonthDay: d.venceDia,
		billableMonths: d.meses,
		studentsLimit: d.max,
		studentsMinimum: d.min,
		periodId: d.periodoId,
		startDate: d.inicio,
		levelId: d.nivelId,
		statusId: d.statusId,
		classroomId: d.aulaId,
		hours: horasV1(d.sesiones),
		seccion: d.seccion
	};
	if (d.inscripcionPropia !== null) record.subscriptionPriceOverride = d.inscripcionPropia;
	if (d.maestraId) record.mentorId = d.maestraId;
	if (d.forzarMotivo) record.forzarChoque = { motivo: d.forzarMotivo };
	const opciones = { headers: { "Content-Type": "application/json", Authorization: "Bearer " + store.getState().sessionToken.value } };
	return d.id == null
		? SchedulesService.registerSchedule(d.cursoOriginal, d.nivelOriginal, record, opciones)
		: SchedulesService.updateSchedule(d.cursoOriginal, d.nivelOriginal, record, opciones, d.id);
};

/** 422 se muestra tal cual lo dice el servidor. */
export const mensajeServidor = (e: any): { texto: string; conflictos: Conflicto[] } => {
	const d = e?.response?.data;
	if (d && typeof d.message === "string" && d.message.trim() !== "" && !/^(Server Error|The given data was invalid\.?)$/i.test(d.message.trim())) {
		const extra = d.errors && typeof d.errors === "object" ? " " + Object.values(d.errors).flat().filter((x: any) => typeof x === "string").join(" ") : "";
		return { texto: (d.message + extra).trim(), conflictos: Array.isArray(d.conflictos) ? d.conflictos : [] };
	}
	return { texto: "", conflictos: [] };
};
